import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { ShieldCheck, AlertCircle, RotateCcw, CheckCircle2, Mail, Smartphone, BellRing, UserCheck, Clock, Ban, Hourglass, Fingerprint } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
// A factory, not a hook — same import style as PickFromMasterModal.
import createAuthedAxios from '../../hooks/useAxiosAuth';
import ModalShell from '../common/ModalShell';
import baseURL from '../../utils/baseURL';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { formatTime } from '../../utils/clock';
import {
    createMocLinkRequest, resendMocLinkOtp, confirmMocLinkRequest, cancelMocLinkRequest,
} from '../../redux/mocLinkSlice';

// GIVING A MITRA ONE CARE PERSON A NUMBER HERE — the one popup every desk
// form shares.
//
// A patient belongs to Mitra One Care, not to a hospital: one human is ONE
// `patients` row, and a connected hospital never copies it — it ALLOCATES its
// own patient number to it (doc/one-patient-row.md §1). "Linking" in this file
// means exactly that allocation, which is why nothing here says a record is
// created. Three things can come back from POST /moc-link/lookup for an
// email/phone typed at the desk, and this modal renders each:
//
//   inTenant  → NOT rendered here. It now means "this person already has a
//               patient number at this hospital", which is not a question for
//               the desk: the forms confirm it inline (green) and open them.
//               The old third state — a record here belonging to no account —
//               cannot occur any more, so there is no row mode left.
//   pending   → a request for this person is already waiting: enter the code /
//               resend / cancel instead of raising another.
//   moc[]     → the person has a Mitra One Care profile: pick which one
//               (several can match a shared phone), then the consent method —
//               Email OTP, Mobile OTP, Approval in the patient app, or Desk.
//               OTP channels confirm on the spot; Approval is async (7-day
//               expiry) and the desk is told by socket when they answer.
//
// DESK (owner, 22 Sep 2026). The patient is standing at the counter, so no code
// has to travel anywhere: a staff member writes WHY they are sure it is them
// and the server allocates on the spot, keeping the reason as an attestation in
// the audit log. It is deliberately the last option and says out loud that the
// patient sees it in their app and can dispute it — an attestation is only
// worth anything if the person it is about can contradict it.
//
// Two ways in:
//   desk mode — `lookup` (already fetched) + `draftPatient` (what was typed).
//               The draft FILLS BLANKS on an unclaimed person and is ignored
//               once they have signed in (§10a.4) — it never overwrites the
//               profile they maintain.
//   `existingRequest` — the Pending-links panel re-opens a request at its
//               code step.

// Hospital name for the "Link with {hospital}?" line — from the public tenant
// list, cached per tenant so every popup doesn't refetch it.
const tenantNameCache = new Map();
const fetchHospitalName = () => {
    const tenantId = localStorage.getItem('TenantId') || '';
    if (!tenantId) return Promise.resolve('');
    if (!tenantNameCache.has(tenantId)) {
        const p = createAuthedAxios().get(`${baseURL}/tenants/public`)
            .then((res) => (res.data?.tenants || []).find((x) => x.tenantId === tenantId)?.name || '')
            .catch(() => { tenantNameCache.delete(tenantId); return ''; });
        tenantNameCache.set(tenantId, p);
    }
    return tenantNameCache.get(tenantId);
};

const fullName = (o) => [o?.name, o?.surname].filter(Boolean).join(' ').trim();

const fmtDateTime = (iso, lang) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    // The time part is the one clock format ("2:30 PM", utils/clock) in every language.
    const day = d.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    return `${day}, ${formatTime(d)}`;
};

const secondsLeft = (iso) => {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    return Number.isNaN(ms) ? null : Math.max(0, Math.floor(ms / 1000));
};

const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

const primaryBtn = 'px-5 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg text-sm font-semibold shadow-md hover:from-[#3AA8A8] hover:to-[#2D8A8A] transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/60';
const secondaryBtn = 'px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-gray-300';
const dangerLink = 'flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50 cursor-pointer';

const MocLinkModal = ({
    open,
    onClose,
    lookup = null,
    patientName = '',
    draftPatient = null,
    existingRequest = null,
    hospitalName: hospitalNameProp = '',
    // OPEN PLATFORM ONLY (classifyMocLookup 'doctorConsent'). The record and
    // the account link already exist here; what needs consent is a
    // doctor_patient row, because tenant #1's doctors are independent and share
    // one patients row per person. Same three channels, same code step — only
    // the account is already known (no candidate list) and the sentence is
    // about the doctor rather than the hospital.
    consentKind = 'link',
    consentDoctorId = null,
    consentDoctorName = '',
    // Case-A exit by id: the server says the account/email already belongs to
    // a patient row HERE (409 ALREADY_LINKED / EMAIL_IN_USE { patientId }).
    onUsePatient = null,
    usePatientLabel = '',
    onLinked,
    onApprovalSent,
    onRequestChanged,
}) => {
    const { t, i18n } = useTranslation('mocLink');
    const dispatch = useDispatch();
    const lang = i18n.language || 'en';

    // loading (the first paint only) | pick | desk | otp | approval_sent |
    // approval_pending | done | no_match | not_connected | no_contact |
    // use_existing (409 ALREADY_LINKED / EMAIL_IN_USE → open that patient) |
    // request_expired (RESEND_LIMIT / OTP_LOCKED / EXPIRED → start again)
    // There is no 'error' phase any more: it belonged to the lookup this
    // popup used to run for itself in row mode, and the caller now always
    // hands the answer in. Every remaining failure is a message on the step
    // the desk is standing on, which is where it can actually be acted on.
    const [phase, setPhase] = useState('loading');
    const [data, setData] = useState(null);           // the lookup result
    const [selectedId, setSelectedId] = useState(null); // chosen MOC candidate
    const [linkRequest, setLinkRequest] = useState(null);
    const [fromPending, setFromPending] = useState(false);
    const [otpCode, setOtpCode] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [left, setLeft] = useState(null);
    const [hospitalName, setHospitalName] = useState(hospitalNameProp);
    const [smsUnavailable, setSmsUnavailable] = useState(false); // 400 SMS_UNAVAILABLE → hide Mobile OTP
    const [resendLeft, setResendLeft] = useState(0);             // seconds until Resend is allowed again
    const [useExisting, setUseExisting] = useState(null);        // { code, patientId } for the case-A exit
    const [attestReason, setAttestReason] = useState('');        // desk channel: why the staff member is sure
    const changedRef = useRef(false);   // a request was cancelled/created → caller re-checks on close
    const otpInputRef = useRef(null);
    const primaryRef = useRef(null);

    const msgOf = useCallback((err, fallback) => extractErrorMessage(err, fallback, lang), [lang]);
    // Localised text for the codes the desk must act on; anything else shows
    // the server's message.
    const codeMessage = useCallback((err, fallback) => {
        switch (err?.code) {
            case 'SMS_UNAVAILABLE': return t('mocLink.sms_unavailable', 'SMS delivery is not configured here — use Email OTP or Approval instead.');
            case 'TOO_MANY_REQUESTS': return t('mocLink.too_many_requests', 'Too many link requests for this account in the last hour — try again later.');
            case 'ATTEST_REASON_TOO_SHORT': return t('mocLink.attest_too_short', 'Write at least a few words about why you are confirming this in person.');
            case 'ATTEST_RATE_LIMITED': return t('mocLink.attest_rate_limited', 'Too many desk confirmations from this account in the last hour — use an OTP or an approval instead.');
            case 'RESEND_LIMIT': return t('mocLink.resend_limit', 'Too many resends — this request has expired. Start again.');
            case 'OTP_LOCKED': return t('mocLink.otp_locked', 'Too many wrong codes — this request has expired. Start again.');
            case 'OTP_EXPIRED':
            case 'EXPIRED': return t('mocLink.request_expired', 'This request has expired. Start again.');
            case 'OTP_MISMATCH': return t('mocLink.confirm_failed', 'The code is not right');
            default: return msgOf(err, fallback);
        }
    }, [t, msgOf]);

    const isDoctorConsent = consentKind === 'doctor';

    const applyLookup = useCallback((lk) => {
        setData(lk);
        const inTenant = lk?.inTenant || null;
        // DOCTOR CONSENT: there is nothing to pick. The account is the one the
        // record here already belongs to (lookup.inTenant.mocAccountId), so the
        // popup opens straight on "how will they confirm?".
        if (isDoctorConsent) {
            if (!inTenant?.mocAccountId) { setPhase('no_match'); return; }
            setSelectedId(inTenant.mocAccountId);
            setPhase('pick');
            return;
        }
        // `inTenant` — "already has a patient number here" — is not a question
        // for the desk: the forms confirm it inline and open that patient, so
        // they never raise this popup for it and it is not rendered here.
        if (lk?.pending) {
            setLinkRequest({ ...lk.pending, contact: lk.pending.contact || null });
            setFromPending(true);
            setPhase(lk.pending.channel === 'approval' ? 'approval_pending' : 'otp');
            return;
        }
        if (!lk?.tenantConnected) { setPhase('not_connected'); return; }
        const moc = lk.moc || [];
        if (!moc.length) { setPhase('no_match'); return; }
        setSelectedId(moc.length === 1 ? moc[0].mocAccountId : null);
        setPhase('pick');
    }, [isDoctorConsent]);

    // (Re)initialise every time the popup opens. Deliberately NOT on `lookup`
    // identity changes while open — that would wipe the desk user's progress.
    useEffect(() => {
        if (!open) return undefined;
        let alive = true;
        setError(''); setOtpCode(''); setBusy(false); setSelectedId(null);
        setLinkRequest(null); setFromPending(false); setLeft(null);
        setSmsUnavailable(false); setResendLeft(0); setUseExisting(null);
        setAttestReason('');
        changedRef.current = false;
        if (!hospitalNameProp) fetchHospitalName().then((n) => { if (alive && n) setHospitalName(n); });
        else setHospitalName(hospitalNameProp);

        if (existingRequest) {
            setLinkRequest(existingRequest);
            setFromPending(true);
            setPhase(existingRequest.channel === 'approval' ? 'approval_pending' : 'otp');
            return () => { alive = false; };
        }
        if (lookup) { applyLookup(lookup); return () => { alive = false; }; }
        setPhase('no_contact');
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // OTP expiry countdown (30 min server-side; the request carries expiresAt).
    useEffect(() => {
        if (!open || phase !== 'otp' || !linkRequest?.expiresAt) { setLeft(null); return undefined; }
        const tick = () => setLeft(secondsLeft(linkRequest.expiresAt));
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [open, phase, linkRequest?.expiresAt]);

    // Resend cooldown (60 s server-side; 429 RESEND_TOO_SOON carries retryAfterMs).
    useEffect(() => {
        if (!open || resendLeft <= 0) return undefined;
        const id = setInterval(() => setResendLeft((n) => (n <= 1 ? 0 : n - 1)), 1000);
        return () => clearInterval(id);
    }, [open, resendLeft]);

    // Focus management: the code box on the OTP step, the primary action elsewhere.
    useEffect(() => {
        if (!open) return;
        const el = phase === 'otp' ? otpInputRef.current : primaryRef.current;
        if (el) setTimeout(() => el.focus(), 30);
    }, [open, phase]);

    const close = useCallback(() => {
        onClose?.({ changed: changedRef.current });
    }, [onClose]);

    // Escape closes, like the rest of the app's dialogs.
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, close]);

    const startRequest = async (channel) => {
        if (!selectedId) { setError(t('mocLink.pick_candidate_first', 'Pick which account is the patient\'s first')); return; }
        // The desk channel carries its attestation instead of a delivery
        // target, and the server holds it to 10 characters — refuse here too so
        // the staff member is not told off by a round trip.
        const reason = attestReason.trim();
        if (channel === 'desk' && reason.length < 10) {
            setError(t('mocLink.attest_too_short', 'Write at least a few words about why you are confirming this in person.'));
            return;
        }
        setBusy(true); setError('');
        try {
            const res = await dispatch(createMocLinkRequest({
                mocAccountId: selectedId,
                channel,
                ...(channel === 'desk' ? { attestReason: reason } : null),
                ...(isDoctorConsent
                    // No draft: the person exists and already has a number
                    // here. patientId is sent so the server can refuse a
                    // consent pointed at somebody else.
                    ? { kind: 'doctor', doctorId: consentDoctorId, patientId: data?.inTenant?.patientId || null }
                    : { draftPatient: draftPatient || {} }),
            }));
            if (createMocLinkRequest.fulfilled.match(res)) {
                changedRef.current = true;
                onRequestChanged?.();
                setLinkRequest(res.payload);
                setFromPending(false);
                // A desk attestation is already settled — there is no code to
                // enter and nothing to wait for, so it ends where an OTP ends.
                if (channel === 'desk') {
                    setPhase('done');
                    toast.success(t('mocLink.linked', 'They now have a patient number here'));
                    onLinked?.(res.payload.patient || null, res.payload.link || null, res.payload);
                    return;
                }
                if (channel !== 'approval') setResendLeft(60);
                setPhase(channel === 'approval' ? 'approval_sent' : 'otp');
            } else if (res.payload?.code === 'REQUEST_PENDING' && res.payload.linkRequest?.linkRequestId) {
                // A live request already waits for this account — jump to its
                // code step instead of dead-ending on the error.
                setLinkRequest(res.payload.linkRequest);
                setFromPending(true);
                setPhase(res.payload.linkRequest.channel === 'approval' ? 'approval_pending' : 'otp');
            } else if ((res.payload?.code === 'ALREADY_LINKED' || res.payload?.code === 'EMAIL_IN_USE') && res.payload.patientId) {
                // The account (or the draft's email) already belongs to a patient
                // row HERE — same exit as case A: open / use that patient.
                setUseExisting({ code: res.payload.code, patientId: res.payload.patientId });
                setPhase('use_existing');
            } else if (res.payload?.code === 'SMS_UNAVAILABLE') {
                // No deliverable gateway: keep Email OTP / Approval on offer.
                setSmsUnavailable(true);
                setError(codeMessage(res.payload));
            } else {
                setError(codeMessage(res.payload, t('mocLink.request_failed', 'Could not send the consent request')));
            }
        } finally {
            setBusy(false);
        }
    };

    const confirm = async () => {
        if (otpCode.length < 6 || busy) return;
        setBusy(true); setError('');
        try {
            const res = await dispatch(confirmMocLinkRequest({ linkRequestId: linkRequest.linkRequestId, otpCode }));
            if (confirmMocLinkRequest.fulfilled.match(res)) {
                changedRef.current = true;
                setPhase('done');
                toast.success(t('mocLink.linked', 'They now have a patient number here'));
                onLinked?.(res.payload.patient || null, res.payload.link || null, linkRequest);
            } else if ((res.payload?.code === 'ALREADY_LINKED' || res.payload?.code === 'EMAIL_IN_USE') && res.payload.patientId) {
                setUseExisting({ code: res.payload.code, patientId: res.payload.patientId });
                setPhase('use_existing');
            } else if (['OTP_LOCKED', 'OTP_EXPIRED', 'EXPIRED', 'ALREADY_DECIDED'].includes(res.payload?.code)) {
                // OTP_LOCKED expires the request server-side — a resend cannot
                // re-arm it; the desk has to start again.
                setError(codeMessage(res.payload));
                setPhase('request_expired');
            } else {
                setError(codeMessage(res.payload, t('mocLink.confirm_failed', 'The code is not right')));
            }
        } finally {
            setBusy(false);
        }
    };

    const resend = async () => {
        if (resendLeft > 0) return;
        setBusy(true); setError('');
        try {
            const res = await dispatch(resendMocLinkOtp({ linkRequestId: linkRequest.linkRequestId }));
            if (resendMocLinkOtp.fulfilled.match(res)) {
                setLinkRequest((prev) => ({ ...prev, ...res.payload }));
                setFromPending(false);
                setOtpCode('');
                setResendLeft(60);
                toast.info(t('mocLink.resent', 'Code re-sent'));
                otpInputRef.current?.focus();
            } else if (res.payload?.code === 'RESEND_TOO_SOON') {
                setResendLeft(Math.max(1, Math.ceil((Number(res.payload.retryAfterMs) || 60000) / 1000)));
            } else if (['RESEND_LIMIT', 'EXPIRED', 'ALREADY_DECIDED'].includes(res.payload?.code)) {
                setError(codeMessage(res.payload));
                setPhase('request_expired');
            } else if (res.payload?.code === 'SMS_UNAVAILABLE') {
                setSmsUnavailable(true);
                setError(codeMessage(res.payload));
            } else {
                setError(codeMessage(res.payload, t('mocLink.resend_failed', 'Could not resend')));
            }
        } finally {
            setBusy(false);
        }
    };

    const cancelRequest = async () => {
        setBusy(true); setError('');
        try {
            const res = await dispatch(cancelMocLinkRequest({ linkRequestId: linkRequest.linkRequestId }));
            if (cancelMocLinkRequest.fulfilled.match(res)) {
                changedRef.current = true;
                onRequestChanged?.();
                toast.info(t('mocLink.cancelled', 'Link request cancelled'));
                setLinkRequest(null); setFromPending(false); setOtpCode('');
                const moc = data?.moc || [];
                if (moc.length) { setSelectedId(moc.length === 1 ? moc[0].mocAccountId : null); setPhase('pick'); }
                else close();
            } else if (['ALREADY_DECIDED', 'EXPIRED'].includes(res.payload?.code)) {
                // Nothing live to withdraw — same outcome as a cancel.
                changedRef.current = true;
                onRequestChanged?.();
                startAgain();
            } else {
                setError(msgOf(res.payload, t('mocLink.cancel_failed', 'Could not cancel the request')));
            }
        } finally {
            setBusy(false);
        }
    };

    // After an expired/withdrawn request: back to the method chooser when
    // there are candidates, otherwise close.
    const startAgain = () => {
        setLinkRequest(null); setFromPending(false); setOtpCode(''); setError(''); setResendLeft(0);
        const moc = data?.moc || [];
        if (moc.length) { setSelectedId(moc.length === 1 ? moc[0].mocAccountId : null); setPhase('pick'); }
        else close();
    };

    if (!open) return null;

    const moc = data?.moc || [];
    const inTenant = data?.inTenant || null;
    // For a doctor consent the account is not in moc[] (that list matches on
    // typed contact details; this one is the account the RECORD belongs to), so
    // the channel buttons are told what it has from the row's masked contacts —
    // present means "there is one", which is all they need to decide.
    const candidate = moc.find((m) => m.mocAccountId === selectedId)
        || (isDoctorConsent && inTenant
            ? { mocAccountId: inTenant.mocAccountId, name: inTenant.name, surname: inTenant.surname,
                email: inTenant.email, phone: inTenant.phone,
                hasEmail: Boolean(inTenant.email), hasPhone: Boolean(inTenant.phone) }
            : null);
    const hospital = hospitalName || t('mocLink.this_hospital', 'this hospital');
    const channelLabel = (ch) => (ch === 'email' ? t('mocLink.channel_email', 'Email OTP') : ch === 'sms' ? t('mocLink.channel_sms', 'Mobile OTP') : ch === 'desk' ? t('mocLink.channel_desk', 'Patient is here — add them now') : t('mocLink.channel_approval', 'Approval in patient app'));

    const subtitle = patientName || fullName(draftPatient) || fullName(inTenant) || '';

    return (
        <ModalShell
            title={t('mocLink.title', 'Give a patient number here')}
            onClose={close}
            closeLabel={t('mocLink.close', 'Close')}
            size="md"
            zClass="z-[60]"
            testId="moc-link-modal"
            closeTestId="moc-link-close"
        >
                    {subtitle && <p className="text-xs text-gray-400 -mt-1 mb-3">{subtitle}</p>}
                    {phase === 'loading' && (
                        <div className="py-8 flex flex-col items-center gap-3">
                            <div className="border-4 border-[#4ABDBD] border-t-transparent rounded-full w-10 h-10 animate-spin"></div>
                            <p className="text-sm text-gray-500">{t('mocLink.checking', 'Checking for a matching account…')}</p>
                        </div>
                    )}

                    {phase === 'not_connected' && (
                        <div className="py-6 text-center">
                            <ShieldCheck size={36} className="mx-auto text-gray-300 mb-3" />
                            <p className="text-sm text-gray-600 font-medium">{t('mocLink.not_connected', 'Not applicable — this hospital runs in isolated mode')}</p>
                        </div>
                    )}

                    {phase === 'no_contact' && (
                        <div className="py-6 text-center">
                            <AlertCircle size={36} className="mx-auto text-gray-300 mb-3" />
                            <p className="text-sm text-gray-600">{t('mocLink.no_contact', 'This patient has no email or phone on record to match an account with.')}</p>
                        </div>
                    )}

                    {phase === 'no_match' && (
                        <div className="py-6 text-center">
                            <ShieldCheck size={36} className="mx-auto text-gray-300 mb-3" />
                            <p className="text-sm text-gray-600">{t('mocLink.no_match', 'No Mitra One Care profile matches this phone or email.')}</p>
                            <p className="text-xs text-gray-400 mt-1">{t('mocLink.no_match_hint', 'Save the form to create one for them. It stays unclaimed — and editable here — until they sign in to the patient app themselves.')}</p>
                        </div>
                    )}

                    {/* CASE B — exists in Mitra One Care, not here: pick + choose the consent method. */}
                    {phase === 'pick' && (
                        <>
                            <p className="text-sm font-semibold text-gray-800 mb-1">
                                {isDoctorConsent
                                    ? t('mocLink.doctor_title', {
                                        doctor: consentDoctorName || t('mocLink.this_doctor', 'this doctor'),
                                        defaultValue: `This patient has a Mitra One Care account, but is not ${consentDoctorName || 'this doctor'}'s patient yet. Ask them?`,
                                    })
                                    : t('mocLink.moc_title', { hospital, defaultValue: `This person already has a Mitra One Care profile. Give them a patient number at ${hospital}?` })}
                            </p>
                            <p className="text-xs text-gray-500 mb-3">
                                {isDoctorConsent
                                    ? t('mocLink.doctor_hint', 'Their record here belongs to them, not to any one doctor — they decide who may see it. An OTP is instant; an approval waits in their app.')
                                    : moc.length > 1
                                        ? t('mocLink.pick_hint_many', 'Several profiles match this number — ask the patient which one is theirs, then choose how they confirm.')
                                        : t('mocLink.pick_hint', 'Ask the patient to confirm it is theirs — a consent code goes to their own contact, or a request to their app. Their profile does not change; this hospital simply gets a number for them.')}
                            </p>
                            {!isDoctorConsent && (
                            <div className="space-y-2 mb-4" data-testid="moc-link-candidates">
                                {moc.map((m) => {
                                    const active = m.mocAccountId === selectedId;
                                    return (
                                        <button
                                            key={m.mocAccountId}
                                            type="button"
                                            onClick={() => { setSelectedId(m.mocAccountId); setError(''); }}
                                            disabled={busy}
                                            aria-pressed={active}
                                            className={`w-full flex items-center gap-3 border rounded-lg p-3 text-left transition-all duration-200 cursor-pointer ${active ? 'border-[#4ABDBD] bg-[#4ABDBD]/5 shadow-sm' : 'border-gray-200 hover:border-[#4ABDBD]/60'}`}
                                            data-testid={`moc-link-candidate-${m.mocAccountId}`}
                                        >
                                            <span className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${active ? 'border-[#4ABDBD]' : 'border-gray-300'}`}>
                                                {active && <span className="w-2 h-2 rounded-full bg-[#4ABDBD]" />}
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-semibold text-gray-800">{fullName(m) || t('mocLink.unnamed', 'Unnamed patient')}</p>
                                                <p className="text-xs text-gray-400 break-all">{[m.email, m.phone].filter(Boolean).join(' · ')}</p>
                                            </div>
                                            {m.matchedOn && (
                                                <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-semibold shrink-0">
                                                    {m.matchedOn === 'phone' ? t('mocLink.matched_phone', 'phone') : t('mocLink.matched_email', 'email')}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                            )}

                            <p className="text-xs font-semibold text-gray-700 mb-2">{t('mocLink.choose_method', 'How will the patient confirm?')}</p>
                            <div className="grid grid-cols-1 gap-2" data-testid="moc-link-methods">
                                <button type="button" ref={primaryRef} onClick={() => startRequest('email')} disabled={busy || !candidate?.hasEmail}
                                    className="flex items-center gap-3 border border-gray-200 rounded-lg p-3 text-left hover:border-[#4ABDBD] hover:shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/60"
                                    data-testid="moc-link-method-email">
                                    <Mail size={18} className="text-[#2D8A8A] shrink-0" />
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm font-semibold text-gray-800">{t('mocLink.channel_email', 'Email OTP')}</span>
                                        <span className="block text-[11px] text-gray-400 break-all">{candidate?.hasEmail ? candidate.email : t('mocLink.no_email', 'No email on the account')}</span>
                                    </span>
                                </button>
                                {!smsUnavailable && (
                                <button type="button" onClick={() => startRequest('sms')} disabled={busy || !candidate?.hasPhone}
                                    className="flex items-center gap-3 border border-gray-200 rounded-lg p-3 text-left hover:border-[#4ABDBD] hover:shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/60"
                                    data-testid="moc-link-method-sms">
                                    <Smartphone size={18} className="text-[#2D8A8A] shrink-0" />
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm font-semibold text-gray-800">{t('mocLink.channel_sms', 'Mobile OTP')}</span>
                                        <span className="block text-[11px] text-gray-400">{candidate?.hasPhone ? candidate.phone : t('mocLink.no_phone', 'No mobile number on the account')}</span>
                                    </span>
                                </button>
                                )}
                                <button type="button" onClick={() => startRequest('approval')} disabled={busy}
                                    className="flex items-center gap-3 border border-gray-200 rounded-lg p-3 text-left hover:border-[#D7A05C] hover:shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#D7A05C]/60"
                                    data-testid="moc-link-method-approval">
                                    <BellRing size={18} className="text-[#D7A05C] shrink-0" />
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm font-semibold text-gray-800">{t('mocLink.channel_approval', 'Approval in patient app')}</span>
                                        <span className="block text-[11px] text-gray-400">{t('mocLink.approval_sub', 'The patient accepts in their app — you are notified. Booking only after they accept.')}</span>
                                    </span>
                                </button>
                                {/* DESK. Only for a hospital link — a doctor-consent ask is
                                    about a doctor the patient may not have met, so it keeps
                                    its three answerable channels. */}
                                {!isDoctorConsent && (
                                <button type="button" onClick={() => { setError(''); setPhase('desk'); }} disabled={busy || !selectedId}
                                    className="flex items-center gap-3 border border-gray-200 rounded-lg p-3 text-left hover:border-[#9DB3B1] hover:shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#9DB3B1]/60"
                                    data-testid="moc-link-method-desk">
                                    <Fingerprint size={18} className="text-[#2D8A8A] shrink-0" />
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm font-semibold text-gray-800">{t('mocLink.channel_desk', 'Patient is here — add them now')}</span>
                                        <span className="block text-[11px] text-gray-400">{t('mocLink.desk_sub', 'You confirm it in person. The patient will see this in their app and can dispute it.')}</span>
                                    </span>
                                </button>
                                )}
                            </div>
                            {busy && (
                                <p className="mt-3 text-xs text-gray-400 flex items-center gap-2">
                                    <span className="border-2 border-[#4ABDBD] border-t-transparent rounded-full w-3.5 h-3.5 animate-spin"></span>
                                    {t('mocLink.sending', 'Sending…')}
                                </p>
                            )}
                        </>
                    )}

                    {/* DESK step — the attestation. No code travels: what makes this
                        honest is the written reason, kept in the audit log, and the
                        patient's own right to dispute it from their app. */}
                    {phase === 'desk' && (
                        <form onSubmit={(e) => { e.preventDefault(); startRequest('desk'); }} data-testid="moc-link-desk-form">
                            <p className="text-sm font-semibold text-gray-800 mb-1">{t('mocLink.desk_title', 'Confirming in person')}</p>
                            <p className="text-xs text-gray-500 mb-3">
                                {t('mocLink.desk_hint', { hospital, defaultValue: `You are giving this Mitra One Care profile a patient number at ${hospital} on your own word, without a code. Say why you are sure it is them.` })}
                            </p>
                            <textarea
                                ref={primaryRef}
                                value={attestReason}
                                onChange={(e) => { setAttestReason(e.target.value.slice(0, 2000)); setError(''); }}
                                rows={3}
                                maxLength={2000}
                                aria-label={t('mocLink.desk_reason_label', 'Why you are confirming this in person')}
                                placeholder={t('mocLink.desk_reason_placeholder', 'Why you are confirming this in person, e.g. patient showed ID at the desk')}
                                className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all text-sm resize-none"
                                data-testid="moc-link-desk-reason"
                            />
                            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-3">
                                <ShieldCheck size={14} className="mt-0.5 shrink-0" />
                                <span>{t('mocLink.desk_warning', 'The patient will see this in their app and can dispute it. Your name and this reason are kept with the link.')}</span>
                            </div>
                            <div className="flex items-center justify-between mt-4">
                                <button type="button" onClick={() => { setError(''); setPhase('pick'); }} disabled={busy} className={secondaryBtn} data-testid="moc-link-desk-back">
                                    {t('mocLink.back', 'Back')}
                                </button>
                                <button type="submit" disabled={busy || attestReason.trim().length < 10} className={primaryBtn} data-testid="moc-link-desk-submit">
                                    {busy && <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>}
                                    <Fingerprint size={16} /> {t('mocLink.desk_link_now', 'Add them now')}
                                </button>
                            </div>
                        </form>
                    )}

                    {/* OTP step — a fresh code, or the code of a request that was already pending. */}
                    {phase === 'otp' && linkRequest && (
                        <form onSubmit={(e) => { e.preventDefault(); confirm(); }} data-testid="moc-link-otp-form">
                            <div className={`flex items-start gap-2 text-xs rounded-lg px-3 py-2 mb-3 ${fromPending ? 'text-amber-700 bg-amber-50' : 'text-[#2D8A8A] bg-[#4ABDBD]/10'}`}>
                                {fromPending ? <Hourglass size={14} className="mt-0.5 shrink-0" /> : <ShieldCheck size={14} className="mt-0.5 shrink-0" />}
                                <span>
                                    {fromPending
                                        ? t('mocLink.pending_otp_hint', { channel: channelLabel(linkRequest.channel), expires: fmtDateTime(linkRequest.expiresAt, lang), defaultValue: `A link request is already pending (${channelLabel(linkRequest.channel)}, expires ${fmtDateTime(linkRequest.expiresAt, lang)}). Enter the code the patient received, or resend it.` })
                                        : t('mocLink.otp_sent', { contact: linkRequest.contact || '', defaultValue: `A consent code was sent to ${linkRequest.contact || 'the patient'}. Ask the patient to read it out.` })}
                                </span>
                            </div>
                            {linkRequest.delivery === 'simulated' && (
                                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 mb-3" data-testid="moc-link-simulated-hint">
                                    {t('mocLink.simulated_hint', 'SMS not configured — the code is shown in the server log (123456 in dev).')}
                                </p>
                            )}
                            <input
                                ref={otpInputRef}
                                value={otpCode}
                                onChange={(e) => { setOtpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6)); setError(''); }}
                                placeholder="______"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={6}
                                aria-label={t('mocLink.otp_label', 'Consent code')}
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all text-center text-xl tracking-[8px] font-bold"
                                data-testid="moc-link-otp-input"
                            />
                            <div className="flex items-center justify-between mt-2 text-[11px] text-gray-400">
                                <span className="flex items-center gap-1">
                                    <Clock size={12} />
                                    {left === null
                                        ? ''
                                        : left > 0
                                            ? t('mocLink.expires_in', { time: mmss(left), defaultValue: `Code expires in ${mmss(left)}` })
                                            : t('mocLink.expired', 'Code expired — resend it')}
                                </span>
                                <button type="button" onClick={resend} disabled={busy || resendLeft > 0}
                                    className="flex items-center gap-1 text-xs text-gray-400 hover:text-[#2D8A8A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer" data-testid="moc-link-resend">
                                    <RotateCcw size={12} />
                                    {resendLeft > 0
                                        ? t('mocLink.resend_in', { time: mmss(resendLeft), defaultValue: `Resend in ${mmss(resendLeft)}` })
                                        : t('mocLink.resend', 'Resend code')}
                                </button>
                            </div>
                            <div className="flex items-center justify-between mt-4">
                                <button type="button" onClick={cancelRequest} disabled={busy} className={dangerLink} data-testid="moc-link-cancel-request">
                                    <Ban size={12} /> {t('mocLink.cancel_request', 'Cancel request')}
                                </button>
                                <button type="submit" disabled={busy || otpCode.length < 6 || left === 0} className={primaryBtn} data-testid="moc-link-confirm">
                                    {busy && <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>}
                                    {t('mocLink.confirm', 'Confirm & link')}
                                </button>
                            </div>
                        </form>
                    )}

                    {phase === 'approval_sent' && linkRequest && (
                        <div className="py-4 text-center" data-testid="moc-link-approval-sent">
                            <BellRing size={40} className="mx-auto text-[#D7A05C] mb-3" />
                            <p className="text-sm font-semibold text-gray-800">{t('mocLink.approval_sent_title', 'Request sent to the patient\'s app')}</p>
                            <p className="text-xs text-gray-500 mt-2">
                                {t('mocLink.approval_sent_text', 'You will be notified here as soon as they answer. Booking is possible after they accept.')}
                            </p>
                            {linkRequest.expiresAt && (
                                <p className="text-[11px] text-gray-400 mt-2">{t('mocLink.approval_expires', { date: fmtDateTime(linkRequest.expiresAt, lang), defaultValue: `Expires ${fmtDateTime(linkRequest.expiresAt, lang)}` })}</p>
                            )}
                            <button type="button" ref={primaryRef} onClick={() => { if (onApprovalSent) onApprovalSent(linkRequest); else close(); }} className={`${primaryBtn} mx-auto mt-5`} data-testid="moc-link-approval-ok">
                                {t('mocLink.got_it', 'Got it')}
                            </button>
                        </div>
                    )}

                    {phase === 'approval_pending' && linkRequest && (
                        <div className="py-2" data-testid="moc-link-approval-pending">
                            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mb-4">
                                <Hourglass size={14} className="mt-0.5 shrink-0" />
                                <span>{t('mocLink.pending_approval_hint', { expires: fmtDateTime(linkRequest.expiresAt, lang), defaultValue: `A link request is already waiting in the patient's app (expires ${fmtDateTime(linkRequest.expiresAt, lang)}). You will be notified when they answer.` })}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <button type="button" onClick={cancelRequest} disabled={busy} className={dangerLink} data-testid="moc-link-cancel-request">
                                    <Ban size={12} /> {t('mocLink.cancel_request', 'Cancel request')}
                                </button>
                                <button type="button" ref={primaryRef} onClick={close} className={primaryBtn}>{t('mocLink.close', 'Close')}</button>
                            </div>
                        </div>
                    )}

                    {/* 409 ALREADY_LINKED / EMAIL_IN_USE with a patientId — the record already exists here. */}
                    {phase === 'use_existing' && useExisting && (
                        <div className="py-4 text-center" data-testid="moc-link-use-existing">
                            <UserCheck size={40} className="mx-auto text-[#4ABDBD] mb-3" />
                            <p className="text-sm font-semibold text-gray-800">
                                {useExisting.code === 'EMAIL_IN_USE'
                                    ? t('mocLink.email_in_use_title', 'This email already belongs to another patient here')
                                    : t('mocLink.linked_here_title', 'This person already has a patient number here')}
                            </p>
                            <p className="text-xs text-gray-500 mt-2">
                                {useExisting.code === 'EMAIL_IN_USE'
                                    ? t('mocLink.email_in_use_text', 'Open that patient and book from there.')
                                    : t('mocLink.linked_here_text', 'Nothing to add — open them and carry on.')}
                            </p>
                            <div className="flex items-center justify-center gap-2 mt-5">
                                <button type="button" onClick={close} className={secondaryBtn}>{t('mocLink.close', 'Close')}</button>
                                {onUsePatient && (
                                    <button type="button" ref={primaryRef} onClick={() => onUsePatient({ patientId: useExisting.patientId, code: useExisting.code })} className={primaryBtn} data-testid="moc-link-use-existing-action">
                                        <UserCheck size={16} /> {usePatientLabel || t('mocLink.open_patient', 'Open patient')}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* RESEND_LIMIT / OTP_LOCKED / EXPIRED — the request is gone; start again. */}
                    {phase === 'request_expired' && (
                        <div className="py-4 text-center" data-testid="moc-link-request-expired">
                            <Clock size={40} className="mx-auto text-amber-400 mb-3" />
                            <p className="text-sm font-semibold text-gray-800">{t('mocLink.request_expired_title', 'Request expired')}</p>
                            <p className="text-xs text-gray-500 mt-2">{error || t('mocLink.request_expired', 'This request has expired. Start again.')}</p>
                            <div className="flex items-center justify-center gap-2 mt-5">
                                <button type="button" onClick={close} className={secondaryBtn}>{t('mocLink.close', 'Close')}</button>
                                {(data?.moc || []).length > 0 && (
                                    <button type="button" ref={primaryRef} onClick={() => { changedRef.current = true; onRequestChanged?.(); startAgain(); }} className={primaryBtn} data-testid="moc-link-start-again">
                                        <RotateCcw size={14} /> {t('mocLink.start_again', 'Start again')}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {phase === 'done' && (
                        <div className="py-8 text-center">
                            <CheckCircle2 size={44} className="mx-auto text-[#4ABDBD] mb-3" />
                            <p className="text-sm font-semibold text-gray-800">{t('mocLink.done', 'Done')}</p>
                            <p className="text-xs text-gray-400 mt-1">{t('mocLink.done_hint', 'They now have a patient number here, and see this hospital in their Mitra One Care app.')}</p>
                        </div>
                    )}

                    {error && phase !== 'request_expired' && (
                        <p className="mt-3 text-sm text-red-500 flex items-start gap-1.5" data-testid="moc-link-error">
                            <AlertCircle size={14} className="mt-0.5 shrink-0" /> {error}
                        </p>
                    )}
        </ModalShell>
    );
};

export default MocLinkModal;
